/**
 * Chat Panel Component - 2タブ構成
 * Tab 1: チャット（全体チャット）
 * Tab 2: 秘密メッセージ（配信者との1対1）
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { localizedApiMessage, type LocalizedMessage } from '../lib/api-error-message';
import { usePartyChat } from '../hooks/usePartyChat';
import { useSecretMessages } from '../hooks/useSecretMessages';
import { useMessageTranslation } from '../hooks/useMessageTranslation';
import { apiClient, type ChatMessage, type SecretMessage } from '../lib/api-client';
import ReasonDialog, { type ReasonDialogResult } from './moderation/ReasonDialog';
import { AnalyticsLogger } from '../lib/analytics';

type ChatTab = 'chat' | 'secret';

// Party chat message type from hook
interface PartyChatMessage {
    id: number;
    username: string;
    message: string;
    created_at: string;
    original_lang?: string;
    display_text?: string | null;
}

// Union type for all message types
type AnyMessage = ChatMessage | SecretMessage | PartyChatMessage;

interface ChatPanelProps {
    streamKey: string;
    viewerName: string;
    chatSendEnabled: boolean;
    secretMessageSendEnabled: boolean;
    anonymizeUsernames?: boolean; // のぞきルームでユーザー名を匿名化
    userRole?: 'streamer' | 'viewer';
    onPointsUpdate?: () => void; // ポイント更新コールバック
    onPurchasePoints?: () => void; // ポイント購入モーダルを開くコールバック
    pointsPurchaseCount?: number; // ポイント購入成功カウンター（変更時にエラーをクリア）
    scheduleId?: string; // イベントスケジュールID（UUID、チャット履歴のフィルタリング用）
    canModerateChat?: boolean; // 運営スタッフが視聴中のとき公開チャットの削除操作を表示
}

export default function ChatPanel({
    streamKey,
    viewerName,
    chatSendEnabled,
    secretMessageSendEnabled,
    anonymizeUsernames = false,
    userRole = 'viewer',
    onPointsUpdate,
    onPurchasePoints,
    pointsPurchaseCount = 0,
    scheduleId,
    canModerateChat = false,
}: ChatPanelProps) {
    const { t, i18n } = useTranslation('watch');
    const [currentTab, setCurrentTab] = useState<ChatTab>('chat');
    const [inputValue, setInputValue] = useState('');
    const [secretMessageError, setSecretMessageError] = useState<LocalizedMessage | null>(null);
    const [chatError, setChatError] = useState<LocalizedMessage | null>(null);
    const [deletingMessage, setDeletingMessage] = useState<AnyMessage | null>(null);
    const [removedMessageIds, setRemovedMessageIds] = useState<ReadonlySet<number>>(new Set());
    const [isSending, setIsSending] = useState(false);

    // Translation hook - backend now provides display_text with cached translations
    const {
        isEnglishMode,
        needsTranslation,
        getDisplayText: getTranslatedDisplayText,
        translateForDisplay,
    } = useMessageTranslation();

    // Fallback translation state for messages without backend translation
    const [fallbackTranslations, setFallbackTranslations] = useState<Map<string, string>>(new Map());
    const [translatingMessages, setTranslatingMessages] = useState<Set<string>>(new Set());
    const [failedTranslations, setFailedTranslations] = useState<Set<string>>(new Set()); // Track failed translations to avoid infinite retry

    // ポイント購入成功時にエラーをクリア
    useEffect(() => {
        if (pointsPurchaseCount > 0) {
            setSecretMessageError(null);
        }
    }, [pointsPurchaseCount]);

    // Fallback translation for messages without backend translation (cache miss)
    const translateMessageFallback = useCallback(async (messageId: string | number, text: string, originalLang?: string) => {
        const key = `${currentTab}-${messageId}`;

        // Skip if already has fallback translation, is translating, or has failed
        if (fallbackTranslations.has(key) || translatingMessages.has(key) || failedTranslations.has(key)) {
            return;
        }

        // Mark as translating
        setTranslatingMessages(prev => new Set(prev).add(key));

        try {
            const translatedText = await translateForDisplay(messageId, text, originalLang);
            if (translatedText) {
                setFallbackTranslations(prev => new Map(prev).set(key, translatedText));
            } else {
                // No translation returned - mark as failed to avoid retry
                setFailedTranslations(prev => new Set(prev).add(key));
            }
        } catch (error) {
            console.error('Translation error:', error);
            // Mark as failed to avoid infinite retry on error
            setFailedTranslations(prev => new Set(prev).add(key));
        } finally {
            setTranslatingMessages(prev => {
                const next = new Set(prev);
                next.delete(key);
                return next;
            });
        }
    }, [currentTab, translateForDisplay, fallbackTranslations, translatingMessages, failedTranslations]);

    // Clear fallback translations when language changes
    useEffect(() => {
        setFallbackTranslations(new Map());
        setTranslatingMessages(new Set());
        setFailedTranslations(new Set());
    }, [i18n.language]);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const prevPartyChatLengthRef = useRef<number>(0);
    const prevSecretMessagesLengthRef = useRef<number>(0);
    const isUserScrolledUpRef = useRef<boolean>(false);
    const isInitialLoadRef = useRef<boolean>(true); // 初期ロードフラグ（スクロールをスキップ）

    // Party chat (全体チャット)
    const partyChat = usePartyChat(
        streamKey,
        viewerName,
        true,
        scheduleId
    );

    // Secret messages (秘密メッセージ)
    // 配信者: すべての秘密メッセージを取得
    // 視聴者: 自分が送った秘密メッセージのみ取得
    const secretMessages = useSecretMessages(
        streamKey,
        true,
        true, // すべてのユーザーでフェッチを有効化
        scheduleId
    );

    // Trigger fallback translation for messages without backend display_text
    useEffect(() => {
        if (!needsTranslation) {
            return;
        }

        const messages = currentTab === 'chat' ? partyChat.messages : secretMessages.messages;

        messages.forEach((msg) => {
            const originalLang = 'original_lang' in msg ? msg.original_lang : undefined;
            const displayText = 'display_text' in msg ? msg.display_text : undefined;
            const text = 'message_text' in msg ? msg.message_text : ('message' in msg ? msg.message : '');

            // Only trigger fallback translation if:
            // 1. Message is not in user's language
            // 2. Backend didn't provide a translation (display_text is null/undefined or same as original)
            if (text && originalLang !== (isEnglishMode ? 'en' : 'ja') && (!displayText || displayText === text)) {
                translateMessageFallback(msg.id, text, originalLang);
            }
        });
    }, [partyChat.messages, secretMessages.messages, currentTab, needsTranslation, translateMessageFallback, isEnglishMode]);

    // Track if user has scrolled up from bottom
    const handleScroll = () => {
        const container = messagesContainerRef.current;
        if (!container) return;

        // Check if user is at or near the bottom (within 50px)
        const isAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 50;
        isUserScrolledUpRef.current = !isAtBottom;
    };

    // Auto-scroll to bottom only when NEW messages arrive AND user is at bottom
    // Skip auto-scroll on initial load (page refresh)
    useEffect(() => {
        const partyChatLength = partyChat.messages.length;
        const secretMessagesLength = secretMessages.messages.length;

        // Check if new messages arrived (length increased)
        const hasNewPartyMessages = partyChatLength > prevPartyChatLengthRef.current;
        const hasNewSecretMessages = secretMessagesLength > prevSecretMessagesLengthRef.current;

        // Skip auto-scroll on initial load to prevent scrolling to bottom on page refresh
        if (isInitialLoadRef.current) {
            // Once we have initial messages loaded, mark initial load as complete
            if (partyChatLength > 0 || secretMessagesLength > 0) {
                isInitialLoadRef.current = false;
            }
            // Update refs without scrolling
            prevPartyChatLengthRef.current = partyChatLength;
            prevSecretMessagesLengthRef.current = secretMessagesLength;
            return;
        }

        // Only scroll if user is already at bottom (not scrolled up)
        if (!isUserScrolledUpRef.current) {
            if (currentTab === 'chat' && hasNewPartyMessages) {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            } else if (currentTab === 'secret' && hasNewSecretMessages) {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }

        // Update refs
        prevPartyChatLengthRef.current = partyChatLength;
        prevSecretMessagesLengthRef.current = secretMessagesLength;
    }, [partyChat.messages.length, secretMessages.messages.length, currentTab]);

    const handleTabChange = (tab: ChatTab) => {
        const prevTab = currentTab;
        setCurrentTab(tab);
        // 分析イベント: タブ切り替えを追跡
        AnalyticsLogger.getInstance().trackTabChange(tab, prevTab);
    };

    const handleSendMessage = async () => {
        const trimmedMessage = inputValue.trim();
        if (!trimmedMessage) return;

        setIsSending(true);

        try {
            if (currentTab === 'chat') {
                // Send message - hooks now handle original_lang internally
                await partyChat.sendMessage(trimmedMessage);
                setChatError(null);
            } else {
                await secretMessages.sendMessage(trimmedMessage);
                // 秘密メッセージ送信後、ポイント残高を更新
                if (onPointsUpdate) {
                    onPointsUpdate();
                }
                // 送信成功時はエラーをクリア
                setSecretMessageError(null);
            }
            // 分析イベント: チャットメッセージ送信を追跡
            AnalyticsLogger.getInstance().trackChatSend(streamKey, currentTab);
            setInputValue('');
            // After sending, scroll to bottom and reset scroll state
            isUserScrolledUpRef.current = false;
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } catch (error) {
            console.error('Failed to send message:', error);
            // 秘密メッセージ送信エラー時（ポイント不足など）
            if (currentTab === 'secret') {
                setSecretMessageError(localizedApiMessage(error, 'apiErrors.insufficientPointsGeneric'));
            } else {
                // 公開チャットの送信エラー（NGワードによる拒否など）
                setChatError(localizedApiMessage(error, 'chat.sendFailed'));
            }
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const activeMessages = currentTab === 'chat'
        ? partyChat.messages.filter((message) => !removedMessageIds.has(message.id))
        : secretMessages.messages;

    const handleDeleteMessage = async (input: ReasonDialogResult) => {
        if (!deletingMessage) return;
        const messageId = deletingMessage.id;
        await apiClient.takedownChatMessage(messageId, input);
        setRemovedMessageIds((prev) => new Set(prev).add(messageId));
        setDeletingMessage(null);
    };
    // Note: isConnected could be used for connection status indicator in future
    // const isConnected = currentTab === 'chat' ? partyChat.isConnected : true;
    const isSendEnabled = currentTab === 'chat' ? chatSendEnabled : secretMessageSendEnabled;

    const renderUsername = (message: AnyMessage) => {
        // Check if this is a streamer message first
        const isStreamer = isStreamerMessage(message);

        // If anonymize is enabled and it's not a streamer, show ****
        if (anonymizeUsernames && !isStreamer) {
            return '****';
        }

        if ('user_username' in message) { // SecretMessage
            return message.user_username;
        }
        if ('username' in message) { // PartyChatMessage or ChatMessage
            return message.username;
        }
        return 'Unknown';
    };

    const isStreamerMessage = (message: AnyMessage) => {
        if ('is_streamer' in message) {
            return Boolean(message.is_streamer);
        }
        // SecretMessage is always from viewer
        return false;
    };

    const renderTimestamp = (message: AnyMessage) => {
        // Ensure timestamp is treated as UTC (backend returns naive UTC timestamps)
        const timestamp = message.created_at.endsWith('Z') ? message.created_at : message.created_at + 'Z';
        return new Date(timestamp).toLocaleTimeString(i18n.language === 'ja' ? 'ja-JP' : 'en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            timeZone: 'Asia/Tokyo',
        });
    };

    const getMessageText = (message: AnyMessage): string => {
        if ('message_text' in message) { // SecretMessage
            return message.message_text;
        }
        if ('message' in message) { // ChatMessage or PartyChatMessage
            return message.message;
        }
        return '';
    };

    // Get display text - use backend translation, fallback translation, or original
    const getDisplayText = (message: AnyMessage): string => {
        const originalText = getMessageText(message);
        const originalLang = 'original_lang' in message ? message.original_lang : undefined;
        const backendDisplayText = 'display_text' in message ? message.display_text : undefined;

        // Use the translation hook's getDisplayText for messages with translation fields
        if (originalLang !== undefined) {
            const result = getTranslatedDisplayText({
                message: originalText,
                original_lang: originalLang,
                display_text: backendDisplayText,
            });
            if (result !== originalText) {
                return result;
            }
        }

        // Check for fallback translation
        const key = `${currentTab}-${message.id}`;
        if (needsTranslation && fallbackTranslations.has(key)) {
            return fallbackTranslations.get(key) || originalText;
        }

        return originalText;
    };

    // Check if a message is currently being translated
    const isMessageTranslating = (message: AnyMessage): boolean => {
        const key = `${currentTab}-${message.id}`;
        return translatingMessages.has(key);
    };

    return (
        <div className="flex flex-col h-full bg-white border border-gray-200 overflow-hidden">
            {/* Chat Tabs */}
            <div className="flex border-b border-gray-200 bg-white">
                <button
                    onClick={() => handleTabChange('chat')}
                    className={`flex-1 py-3 px-4 font-semibold transition-colors ${currentTab === 'chat'
                        ? 'bg-white text-gray-900 border-b-2 border-pink-500'
                        : 'text-gray-600 hover:bg-gray-50'
                        }`}
                >
                    {t('chat.tab')}{currentTab === 'secret' && partyChat.messages.length > 0 ? `(${partyChat.messages.length})` : ''}
                </button>
                <button
                    onClick={() => handleTabChange('secret')}
                    className={`flex-1 py-3 px-4 font-semibold transition-colors ${currentTab === 'secret'
                        ? 'bg-white text-gray-900 border-b-2 border-pink-500'
                        : 'text-gray-600 hover:bg-gray-50'
                        }`}
                >
                    {t('chat.secretTab')}
                </button>
            </div>

            {/* Status Notice */}
            {currentTab === 'chat' && !chatSendEnabled && (
                <div className="px-4 py-2 bg-yellow-100 text-yellow-800 text-sm border-b border-yellow-200">
                    ⚠️ {t('chat.sendDisabled')}
                </div>
            )}

            {/* Secret Message Header */}
            {currentTab === 'secret' && (
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm text-gray-600">
                    {t('chat.secretHeader')}
                </div>
            )}



            {/* Messages */}
            <div
                ref={messagesContainerRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-thumb-pink-300 scrollbar-track-gray-100 hover:scrollbar-thumb-pink-400"
            >
                {activeMessages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-500">
                        {t('chat.noMessages')}
                    </div>
                ) : (
                    <ul className="space-y-1">
                        {activeMessages.map((msg) => {
                            // 視聴者側の秘密メッセージタブではユーザー名を非表示
                            const hideUsername = currentTab === 'secret' && userRole === 'viewer';

                            return (
                                <li key={msg.id} className="flex items-start gap-2">
                                    {/* バッジ列 - 秘密メッセージでユーザー名非表示時は省略 */}
                                    {!hideUsername && (
                                        <div className="w-8 flex-shrink-0 flex justify-start">
                                            {isStreamerMessage(msg) && (
                                                <span className="px-1.5 py-0.5 text-xs font-medium text-white bg-red-500 rounded inline-flex items-center">
                                                    📡
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {/* ユーザー名列 - 視聴者側の秘密メッセージでは非表示 */}
                                    {!hideUsername && (
                                        <span className="font-semibold text-sm text-gray-800 w-24 flex-shrink-0 truncate text-left">
                                            {renderUsername(msg)}
                                        </span>
                                    )}
                                    {/* 時間列 */}
                                    <span className="text-xs text-gray-600 w-12 flex-shrink-0 text-left">
                                        {renderTimestamp(msg)}
                                    </span>
                                    {/* メッセージ列 */}
                                    <p className={`text-sm break-words flex-1 min-w-0 text-left ${isMessageTranslating(msg) ? 'text-gray-500 italic' : 'text-gray-800'}`}>
                                        {isMessageTranslating(msg) ? (
                                            <>
                                                {getMessageText(msg)}
                                                <span className="ml-1 text-xs text-gray-400">🔄</span>
                                            </>
                                        ) : (
                                            getDisplayText(msg)
                                        )}
                                    </p>
                                    {canModerateChat && currentTab === 'chat' && (
                                        <button
                                            type="button"
                                            onClick={() => setDeletingMessage(msg)}
                                            className="flex-shrink-0 border border-red-600 px-1.5 text-xs text-red-600 hover:bg-red-50"
                                            aria-label={`メッセージ ${msg.id} を削除`}
                                        >
                                            削除
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="px-4 py-2 border-t border-gray-200">
                {/* Secret Message Cost Notice */}
                {currentTab === 'secret' && userRole !== 'streamer' && !secretMessageError && (
                    <div className="mb-2 bg-blue-50 border border-blue-200 px-3 py-1.5 text-sm text-blue-900 flex items-center gap-2">
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        {t('chat.secretCost')}
                    </div>
                )}

                {/* Public chat send error (e.g. NG word rejection) */}
                {currentTab === 'chat' && chatError && (
                    <div className="mb-2 bg-red-50 border border-red-500 px-3 py-2 text-center" role="alert">
                        <div className="text-red-600 font-semibold text-sm">⚠️ {t(chatError.key, chatError.values)}</div>
                    </div>
                )}

                {/* Secret Message Points Error */}
                {currentTab === 'secret' && secretMessageError && (
                    <div className="mb-2 bg-red-50 border border-red-500 px-3 py-2 text-center">
                        <div className="text-red-600 font-semibold text-sm">⚠️ {t(secretMessageError.key, secretMessageError.values)}</div>
                        {onPurchasePoints && (
                            <button
                                onClick={onPurchasePoints}
                                className="mt-2 px-4 py-1.5 bg-pink-500 hover:bg-pink-600 text-white font-semibold text-sm"
                            >
                                {t('chat.purchasePoints')}
                            </button>
                        )}
                    </div>
                )}

                <div className="flex gap-2">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={isSendEnabled ? t('chat.placeholder') : t('chat.placeholderDisabled')}
                        disabled={!isSendEnabled || isSending}
                        className="flex-1 px-3 py-2 border border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <button
                        onClick={handleSendMessage}
                        disabled={!isSendEnabled || isSending}
                        className="px-4 py-2 text-white bg-pink-500 hover:bg-pink-600 focus:outline-none focus:ring-2 focus:ring-pink-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        {isSending ? '🔄' : t('chat.send')}
                    </button>
                </div>
            </div>
            {canModerateChat && (
                <ReasonDialog
                    open={deletingMessage !== null}
                    title="チャットメッセージを削除"
                    description="視聴中の全員の画面とチャット履歴から削除します。"
                    confirmLabel="削除する"
                    onCancel={() => setDeletingMessage(null)}
                    onConfirm={handleDeleteMessage}
                />
            )}
        </div>
    );
}
